import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setBearer, bootstrapBearer, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    bootstrapBearer();
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setBearer(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = e.response
        ? (formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again.")
        : "Couldn't reach the server. Check your connection and try again.";
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (data.access_token) setBearer(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = e.response
        ? (formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again.")
        : "Couldn't reach the server. Check your connection and try again.";
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setBearer(null);
    setUser(null);
  }, []);

  const impersonate = useCallback(async (userId) => {
    setError(null);
    try {
      const { data } = await api.post(`/admin/impersonate/${userId}`);
      if (data.access_token) setBearer(data.access_token);
      // Re-fetch /me to pick up impersonator_id claim
      await refresh();
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  }, [refresh]);

  const value = { user, error, login, register, logout, refresh, impersonate, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
